import React, { useEffect, useRef, useState } from 'react';
import { ContinuosReportingType } from '../ContinuosReporting';
import { Col, Form, Row } from 'react-bootstrap';
import { updateStatementStatusByProjectIdAndStatementId } from 'lib/api_ts/continuosReportingApi';
import { useLocation } from 'react-router-dom-v5-compat';
import { isValid } from 'date-fns';
import { getProject } from '@lib/api_ts/projectApi';
import StatusBar from '@components/StatusBar';
import { useQuery } from '@tanstack/react-query';

interface ContinuosReportingStatusProps {
	data: ContinuosReportingType;
	isBeneficiary: boolean;
	isEnabledToView: boolean;
}

const ContinuosReportingStatus: React.FC<ContinuosReportingStatusProps> = ({ data, isBeneficiary, isEnabledToView }) => {
	const { pathname } = useLocation();
	const projectId = pathname.split('/')[2];
	const canEdit = !isBeneficiary || isEnabledToView;

	const { data: projectData } = useQuery({
		queryKey: ['project', projectId],
		queryFn: () => getProject(projectId),
		enabled: projectId !== undefined,
		refetchOnMount: false,
	});

	const [sbShow, setSbShow] = useState(<></>);

	const [status, setStatus] = useState({
		sent: {
			checked: !!data.sent,
			date: data.sentDate || '',
		},
		approved: {
			checked: !!data.approved,
			date: data.approvedDate || '',
		},
	});

	const rows = [
		{
			name: 'sent',
			checkboxLabel: 'inviato',
			checked: status.sent.checked,
			date: status.sent.date,
		},
		{
			name: 'approved',
			checkboxLabel: 'approvato',
			checked: status.approved.checked,
			date: status.approved.date,
		},
	];

	const isModifiedRef = useRef(false);

	useEffect(() => {
		if (!isModifiedRef.current) return;
		if (new Date(status.sent.date).getFullYear() < 2000 || new Date(status.approved.date).getFullYear() < 2000) return;
		if (
			new Date(status.sent.date) < new Date(projectData?.forecastStartDate) ||
			new Date(status.approved.date) < new Date(projectData?.forecastStartDate)
		) {
			setSbShow(
				<StatusBar status="error" message={'La data inserita non può essere precedente alla data di avvio del progetto'} timeout={false}>
					<></>
				</StatusBar>,
			);
			return;
		}
		try {
			(async () => {
				await updateStatementStatusByProjectIdAndStatementId(projectId, data.id, {
					statementTypeCode: 'CR',
					sent: status.sent.checked,
					sentDate: status.sent.date,
					approved: status.approved.checked,
					approvedDate: status.approved.date,
				});
			})();
		} catch (error) {
			console.error('Error updating statement', error);
		}
	}, [status, data, projectId, projectData?.forecastStartDate]);

	return (
		<Form className="p-4">
			{rows.map((row) => (
				<Row key={row.name} className="align-items-end mb-5">
					<Form.Group as={Col} md={1} className="d-flex align-items-center gap-2 mb-2 me-4">
						<Form.Label className="mb-0 fw-bold">{row.checkboxLabel}</Form.Label>
						<Form.Check
							type="switch"
							checked={row.checked}
							onChange={(event) => {
								if (!isModifiedRef.current) isModifiedRef.current = true;
								setStatus((prevState) => ({
									...prevState,
									[row.name]: {
										...prevState[row.name],
										checked: event.target.checked,
										date: event.target.checked ? prevState[row.name].date : '',
									},
								}));
							}}
							disabled={!canEdit}
						/>
					</Form.Group>
					<Form.Group as={Col} md={3}>
						<Form.Label>In data</Form.Label>
						<Form.Control
							type="date"
							disabled={!status[row.name].checked || !canEdit}
							min={projectData?.forecastStartDate.split('T')[0] ?? ''}
							value={status[row.name].date}
							onChange={(event) => {
								if (!isModifiedRef.current) isModifiedRef.current = true;
								const selectedDate = new Date(event.target.value);
								if (!isValid(selectedDate)) return;
								setStatus((prevState) => ({
									...prevState,
									[row.name]: {
										...prevState[row.name],
										date: event.target.value,
									},
								}));
							}}
						/>
					</Form.Group>
				</Row>
			))}
			{sbShow}
		</Form>
	);
};

export default ContinuosReportingStatus;
